/**
 * @fileoverview Implementación simple de caché en memoria con soporte TTL.
 * Diseñada para entornos serverless / frontend donde Redis no está disponible.
 * Cumple el contrato mínimo { get, set, invalidate, invalidatePattern } usado por los casos de uso.
 * NO se recomienda para producción a gran escala; sustituir por Redis o Firestore TTL.
 */

class MemoryCache {
  constructor(logger = console) {
    this.store = new Map();
    this.logger = logger;
  }

  /**
   * Obtiene un valor de la caché
   * @param {string} key - Clave de caché
   * @returns {Promise<any|null>} Valor almacenado o null si expirado / no existe
   */
  async get(key) {
    const record = this.store.get(key);
    if (!record) return null;

    const { value, expiresAt } = record;
    if (expiresAt && Date.now() > expiresAt) {
      // Expirado, eliminar y devolver null
      this.store.delete(key);
      return null;
    }

    return value;
  }

  /**
   * Almacena un valor con TTL opcional
   * @param {string} key - Clave
   * @param {any} value - Valor a almacenar
   * @param {number} ttlSeconds - Tiempo de vida en segundos (opcional)
   */
  async set(key, value, ttlSeconds = 300) {
    const expiresAt = ttlSeconds > 0 ? Date.now() + ttlSeconds * 1000 : null;
    this.store.set(key, { value, expiresAt });
    // Limpieza automática opcional
    return true;
  }

  /**
   * Invalida una clave concreta
   * @param {string} key
   */
  async invalidate(key) {
    this.store.delete(key);
    return true;
  }

  /**
   * Invalida todas las claves que emparejan un patrón simple (prefijo*).
   * @param {string} pattern - Patrón de prefijo
   */
  async invalidatePattern(pattern) {
    const prefix = pattern.replace('*', '');
    for (const key of this.store.keys()) {
      if (key.startsWith(prefix)) {
        this.store.delete(key);
      }
    }
    return true;
  }
}

export default MemoryCache;
